/**
 * "Blick in die Zukunft" (V-01): die naechsten 7 Tage - Termine nach der
 * Kalender-Kurzuebersicht, Deadlines (F-09), laufende Vorhaben und faellige
 * Reflexionen. Nur anschauen, abhaken und hinklicken (V-02).
 */
import { Link } from 'react-router-dom';
import { Telescope, RotateCcw } from 'lucide-react';

import { Card } from '@/modules/dashboard/Card';
import { TerminZeilen } from '@/modules/dashboard/TerminZeilen';
import { PriorityDot } from '@/ui/composites/PriorityDot';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useAreaColor } from '@/modules/areas/useAreas';
import { useOccurrences } from '@/modules/calendar/hooks/useOccurrences';
import { DeadlineBadge } from '@/modules/deadlines/DeadlineBadge';
import { DEADLINE_ICON } from '@/modules/deadlines/deadlineIcons';
import { useDeadlines } from '@/modules/deadlines/useDeadlines';
import { useMeilensteine } from '@/modules/projects/meilensteine/useMeilensteine';
import { MeilensteinEintrag } from '@/modules/projects/meilensteine/MeilensteinEintrag';
import { anstehendeMeilensteine } from '@/domain/projects/meilensteine';
import { deadlinePfad } from '@/modules/deadlines/useDeadlineOpen';
import { useProjects } from '@/modules/projects/useProjects';
import { useActiveGoals } from '@/modules/goals/useGoals';
import { useReviewDue } from '@/modules/review/useReviewDue';
import { useMonthReviewDue } from '@/modules/review/useMonthReviewDue';
import { useAreaFilter } from '@/store/areaFilter';
import { toggleTaskDone } from '@/db/repositories/tasks';
import { termineZwischen, zukunftsDeadlines } from '@/domain/uebersicht';
import { DEADLINE_KIND_LABEL, deadlineKey } from '@/domain/deadlines';
import { projectPriority, projectPriorityRank } from '@/domain/priority';
import { pfad } from '@/app/fokus';
import { addDays, formatDe } from '@/lib/datetime';
import { de } from '@/i18n/de';
import type { LocalDate, LocalDateTime } from '@/domain/types';

/** So weit schaut der Block voraus (Standardwert aus V-01). */
const TAGE = 7;
/** Die Tage davor zeigt schon die Kalender-Kurzuebersicht. */
const AB_TAG = 3;
const DEADLINE_MAX = 8;
const VORHABEN_MAX = 5;

export function ZukunftBlock({ heute }: { heute: LocalDate }) {
  const von = addDays(heute, AB_TAG);
  const bis = addDays(heute, TAGE + 1);
  const occurrences = useOccurrences({ from: `${von}T00:00` as LocalDateTime, to: `${bis}T00:00` as LocalDateTime });
  const deadlines = zukunftsDeadlines(useDeadlines(), heute, TAGE);
  // Anstehende Meilensteine (F-25, Vorschlag) - nur, wenn es welche gibt.
  const alleMeilensteine = useMeilensteine();
  const meilensteine = anstehendeMeilensteine(alleMeilensteine, heute, TAGE);
  const rueckblickFaellig = useReviewDue();
  const monatFaellig = useMonthReviewDue();

  return (
    <Card title="Blick in die Zukunft" icon={Telescope} to="/kalender">
      <div className="flex flex-col gap-3">
        <Abschnitt titel={`Weitere Termine · ${formatDe(von, 'EEEEEE dd.MM.')} – ${formatDe(addDays(bis, -1), 'EEEEEE dd.MM.')}`}>
          <TerminZeilen liste={termineZwischen(occurrences, von, bis)} leer="Keine weiteren Termine." mitDatum />
        </Abschnitt>

        <Abschnitt titel={`Deadlines · ${TAGE} Tage`}>
          <Deadlines liste={deadlines} />
        </Abschnitt>

        {meilensteine.length > 0 && (
          <Abschnitt titel={`Meilensteine · ${TAGE} Tage`}>
            <ul className="flex flex-col gap-0.5">
              {meilensteine.slice(0, DEADLINE_MAX).map((m) => (
                <li key={m.id} className="flex items-center gap-2 text-sm">
                  <span className="w-[4.5rem] shrink-0 tabular-nums text-[12px] text-ink-faint">{formatDe(m.date, 'EEEEEE dd.MM.')}</span>
                  <span className="min-w-0 flex-1"><MeilensteinEintrag m={m} /></span>
                </li>
              ))}
            </ul>
          </Abschnitt>
        )}

        <Vorhaben />

        {(rueckblickFaellig || monatFaellig) && (
          <Abschnitt titel="Reflexion">
            {rueckblickFaellig && (
              <Hinweis to={`/rueckblick?woche=${rueckblickFaellig.key}`}>{de.review.dashboardNudge(rueckblickFaellig.name)}</Hinweis>
            )}
            {monatFaellig && (
              <Hinweis to={`/rueckblick?monat=${monatFaellig.key}`}>Monatsreflexion für {monatFaellig.name} steht aus</Hinweis>
            )}
          </Abschnitt>
        )}
      </div>
    </Card>
  );
}

function Abschnitt({ titel, children, rechts }: { titel: string; children: React.ReactNode; rechts?: React.ReactNode }) {
  return (
    <section aria-label={titel} className="flex flex-col gap-1 border-t border-line pt-2.5 first:border-t-0 first:pt-0">
      <h3 className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-ink-faint">
        <span className="flex-1">{titel}</span>{rechts}
      </h3>
      {children}
    </section>
  );
}

function Hinweis({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="flex items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-surface-raised">
      <RotateCcw className="size-3.5 shrink-0 text-ink-faint" aria-hidden="true" />
      {children}
    </Link>
  );
}

function Deadlines({ liste }: { liste: ReturnType<typeof zukunftsDeadlines> }) {
  const areaColor = useAreaColor();
  if (liste.length === 0) return <p className="text-sm text-ink-faint">In den nächsten {TAGE} Tagen läuft keine Frist ab.</p>;
  return (
    <ul className="flex flex-col gap-1">
      {liste.slice(0, DEADLINE_MAX).map((d) => {
        const Icon = DEADLINE_ICON[d.kind];
        return (
          <li key={`${d.kind}:${d.id}`} className="flex items-center gap-2 text-sm">
            {/* Abhaken ist erlaubt (Leitlinie 4) - bei Aufgaben. */}
            {d.kind === 'aufgabe' ? (
              <input
                type="checkbox"
                checked={false}
                onChange={() => void toggleTaskDone(d.id)}
                aria-label={`${d.title} erledigt`}
                className="size-4 shrink-0 accent-[var(--color-accent)]"
              />
            ) : (
              <Icon className="size-4 shrink-0" style={{ color: areaColor(d.areaId) }} aria-label={DEADLINE_KIND_LABEL[d.kind]} />
            )}
            <Link to={deadlinePfad(d)} title={DEADLINE_KIND_LABEL[d.kind]} className="min-w-0 flex-1 truncate hover:underline hover:decoration-line-strong">
              {d.title}
            </Link>
            <DeadlineBadge date={d.date} time={d.time} />
          </li>
        );
      })}
      {liste.length > DEADLINE_MAX && <li className="text-[12px] text-ink-faint">+{liste.length - DEADLINE_MAX} weitere</li>}
    </ul>
  );
}

/** Laufende Projekte und Ziele, kompakt - die Details stehen auf ihren Seiten. */
function Vorhaben() {
  const areaColor = useAreaColor();
  const { activeAreaIds } = useAreaFilter();
  const projekte = useProjects()
    .filter((p) => p.project.status === 'aktiv')
    .sort((a, b) =>
      projectPriorityRank(projectPriority(a.project)) - projectPriorityRank(projectPriority(b.project))
      || frist(a.project).localeCompare(frist(b.project))
      || a.project.title.localeCompare(b.project.title, 'de'));
  const ziele = useActiveGoals(20)
    .filter(({ goal }) => activeAreaIds.length === 0 || (goal.areaId != null && activeAreaIds.includes(goal.areaId)));

  return (
    <>
      <Abschnitt titel="Projekte" rechts={<Alle to="/projekte" anzahl={projekte.length} />}>
        {projekte.length === 0 ? <p className="text-sm text-ink-faint">Keine aktiven Projekte.</p> : (
          <ul className="flex flex-col gap-1">
            {projekte.slice(0, VORHABEN_MAX).map(({ project, progress, stuck }) => (
              <li key={project.id} className="flex items-center gap-2 text-sm">
                <PriorityDot priority={projectPriority(project)} />
                <Link to={pfad.projekt(project.id)} className="min-w-0 flex-1 truncate hover:underline">{project.title}</Link>
                {stuck && <span className="shrink-0 text-[11px] text-warn">kein nächster Schritt</span>}
                <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">{progress.done}/{progress.total}</span>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>
      <Abschnitt titel="Ziele" rechts={<Alle to="/ziele" anzahl={ziele.length} />}>
        {ziele.length === 0 ? <p className="text-sm text-ink-faint">Keine laufenden Ziele.</p> : (
          <ul className="flex flex-col gap-1">
            {ziele.slice(0, VORHABEN_MAX).map(({ goal, progress }) => (
              <li key={goal.id} className="flex items-center gap-2 text-sm">
                <AreaDot color={areaColor(goal.areaId)} size="sm" />
                <Link to={pfad.ziel(goal.id)} className="min-w-0 flex-1 truncate hover:underline">{goal.title}</Link>
                <span className="shrink-0 text-[11px] tabular-nums text-ink-faint">{Math.round(progress * 100)} %</span>
              </li>
            ))}
          </ul>
        )}
      </Abschnitt>
    </>
  );
}

function Alle({ to, anzahl }: { to: string; anzahl: number }) {
  if (anzahl <= VORHABEN_MAX) return null;
  return <Link to={to} className="font-normal normal-case tracking-normal hover:text-ink hover:underline">alle {anzahl}</Link>;
}

/** Ohne Deadline ans Ende. */
const frist = (p: { deadline?: string; deadlineTime?: string }) => (p.deadline ? deadlineKey(p.deadline, p.deadlineTime) : '9999');
